import React from "react";

import { renderToStaticMarkup } from "react-dom/server";

import TooltipLines from "@/src/components/tooltip/TooltipLines";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/src/components/ui/avatar";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import prettyDurations from "../../../utils/durations";
import DefaultAvatarImage from "../../common/DefaultAvatarImage";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import { dateTimeColumn, statusColumn } from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { contentPath } from "../../routes/routes";
import StatusTag from "../StatusTag";

import type { TFunction } from "i18next";

import type {
  EncodingJob,
  EncodingStatus,
  EncodingType,
} from "../../../api/EncoderAPI.types";
import type {
  ColumnLayout,
  DataTableColumnDef,
  FilterOption,
  StatusOption,
} from "../../dataTable/dataTable.types";

interface EncodingColumnsOptions {
  t: TFunction;
  hasFastOption: boolean;
}

interface EncodingHrefSource {
  media_uuid: string;
  media: { type: string };
}

const ENCODING_STATUS_LIST: EncodingStatus[] = [
  "transcoding",
  "transcoded",
  "queued",
  "error",
  "canceled",
  "untranscoded",
];

const ENCODING_TYPE_LIST: EncodingType[] = [
  "ott",
  "ott_audio",
  "fast_hls",
  "fast_mp4",
  "d2g",
];

const FAST_TYPE_LIST: EncodingType[] = ["fast_hls", "fast_mp4"];

const SORT_FIELDS: Record<string, string> = {
  mediaDuration: "media_duration",
  mediaTitle: "media_label",
};

const TYPE_LAYOUT: ColumnLayout = {
  width: "14%",
  minWidth: 140,
  align: "center",
};
const DURATION_LAYOUT: ColumnLayout = {
  width: "14%",
  minWidth: 110,
  align: "center",
};
const OWNER_LAYOUT: ColumnLayout = {
  width: "14%",
  minWidth: 85,
  align: "center",
};
const DATE_LAYOUT: ColumnLayout = {
  width: "13%",
  minWidth: 160,
};

export function toEncodingStatus(
  value: string | undefined,
): EncodingStatus | undefined {
  return ENCODING_STATUS_LIST.find((status) => status === value);
}

export function toEncodingType(
  value: string | undefined,
): EncodingType | undefined {
  return ENCODING_TYPE_LIST.find((type) => type === value);
}

export function getEncodingSortField(columnId: string): string {
  return SORT_FIELDS[columnId] ?? columnId;
}

export function getEncodingHref({
  media,
  media_uuid: mediaUuid,
}: EncodingHrefSource): string | undefined {
  return contentPath(media.type, mediaUuid);
}

function getTypeOptions(t: TFunction, hasFastOption: boolean): FilterOption[] {
  return ENCODING_TYPE_LIST.filter(
    (type) => hasFastOption || !FAST_TYPE_LIST.includes(type),
  ).map((type) => ({ value: type, label: t(`editor-encoding:${type}`) }));
}

export function encodingStatusOptions(t: TFunction): StatusOption[] {
  return [
    {
      value: "transcoding",
      label: t("table:status.transcoding"),
      tone: "info",
    },
    {
      value: "transcoded",
      label: t("table:status.transcoded"),
      tone: "success",
    },
    { value: "queued", label: t("table:status.queued"), tone: "warning" },
    { value: "error", label: t("table:status.error"), tone: "error" },
    { value: "canceled", label: t("table:status.canceled"), tone: "neutral" },
    {
      value: "untranscoded",
      label: t("table:status.untranscoded"),
      tone: "neutral",
    },
  ];
}

export function encodingColumns({
  t,
  hasFastOption,
}: EncodingColumnsOptions): DataTableColumnDef<EncodingJob>[] {
  return [
    statusColumn<EncodingJob>("status", t("status"), encodingStatusOptions(t)),
    {
      accessorKey: "type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("type")} />
      ),
      enableSorting: false,
      meta: {
        ...TYPE_LAYOUT,
        label: t("type"),
        filterOptions: getTypeOptions(t, hasFastOption),
      },
      cell: ({ row }) => (
        <span className="inline-flex align-middle">
          <StatusTag>{t(`editor-encoding:${row.original.type}`)}</StatusTag>
        </span>
      ),
    },
    {
      id: "mediaTitle",
      accessorFn: (encoding) => encoding.media.label,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("mediaName")} />
      ),
      meta: { label: t("mediaName") },
      cell: ({ row }) => (
        <DataTableLinkCell href={getEncodingHref(row.original)}>
          {row.original.media.label}
        </DataTableLinkCell>
      ),
    },
    {
      id: "mediaDuration",
      accessorFn: (encoding) => encoding.media.source_duration_ms,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("mediaDuration")} />
      ),
      meta: { ...DURATION_LAYOUT, label: t("mediaDuration") },
      cell: ({ row }) => {
        const duration = row.original.media.source_duration_ms;
        return duration
          ? prettyDurations(duration, { valueUnit: "ms" }).fastDuration
          : "";
      },
    },
    {
      id: "owner",
      accessorFn: (encoding) => encoding.owner.email,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("owner")} />
      ),
      enableSorting: false,
      meta: { ...OWNER_LAYOUT, label: t("owner") },
      cell: ({ row }) => (
        <Avatar
          className="inline-flex size-6.25 align-middle"
          data-tooltip-id={APP_TOOLTIP_ID}
          data-tooltip-html={renderToStaticMarkup(
            <TooltipLines
              lines={[
                `${row.original.owner.first_name} ${row.original.owner.name}`,
                row.original.owner.email,
              ]}
            />,
          )}
        >
          <AvatarImage alt="" src={row.original.owner.avatar ?? undefined} />
          <AvatarFallback>
            <DefaultAvatarImage />
          </AvatarFallback>
        </Avatar>
      ),
    },
    dateTimeColumn<EncodingJob>("date", t("date"), DATE_LAYOUT),
  ];
}
